import { LoadingButton } from '@mui/lab';
import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import FinishOperationModal from 'components/private/Modals/operation/FinishOperationModal';
import RequireOperationSettings from 'components/shared/RequiredOpertaionSettings/RequireOperationSettings';
import SpinnerLoader from 'components/shared/SpinnerLoader/SpinnerLoader';
import { getTransferOperationStatus } from 'hooks/operation/useTransferOperationTableColumns';
import useTransferWorkspace from 'hooks/operation/useTransferWorkspace';
import ProductList from 'pages/Operation/ProductList';
import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { updateQueryParams } from 'utils/updateQueryParams';

import { TransferDock } from './TransferDock';
import { TransferWorkspaceSidebar } from './WorkspaceSidebar';

const SIDEBAR_SECTION = {
  DOCK: 'dock',
  PRODUCT_LIST: 'productList',
};
const TransferOperationWorkspace = () => {
  const { guid } = useParams();

  const [selectedSection, setSelectedSection] = useState({
    type: SIDEBAR_SECTION.DOCK,
  });
  const [isFinishModalOpen, setIsFinishModalOpen] = useState(false);

  const {
    loading,
    actionLoading,
    workspace,
    title,
    submitOperation,
    state,
    canSubmitOperation,
  } = useTransferWorkspace(guid);

  const status = getTransferOperationStatus({
    state,
    taskState: workspace?.taskState,
  });

  const sectionTitle = useMemo(() => {
    switch (selectedSection.type) {
      case SIDEBAR_SECTION.DOCK:
        return 'بارانداز';
      case SIDEBAR_SECTION.PRODUCT_LIST:
        return 'لیست محصول';
      default:
        return '';
    }
  }, [selectedSection]);

  const handleSelectSidebarItem = type => {
    switch (type) {
      case SIDEBAR_SECTION.DOCK:
        setSelectedSection({ type: SIDEBAR_SECTION.DOCK });
        updateQueryParams({
          selectedTab: SIDEBAR_SECTION.DOCK,
        });
        break;

      case SIDEBAR_SECTION.PRODUCT_LIST:
        setSelectedSection({ type: SIDEBAR_SECTION.PRODUCT_LIST });
        updateQueryParams({
          selectedTab: SIDEBAR_SECTION.PRODUCT_LIST,
        });
        break;

      default:
        break;
    }
  };

  const handleConfirmFinishOperation = async () => {
    const ok = await submitOperation();
    if (ok) setIsFinishModalOpen(false);
  };

  // eslint-disable-next-line complexity
  const renderMainContent = () => {
    if (loading && !workspace) return <SpinnerLoader />;
    if (!workspace) {
      return (
        <Typography color="text.secondary" textAlign="center" py={4}>
          اطلاعات عملیات یافت نشد.
        </Typography>
      );
    }

    switch (selectedSection.type) {
      case SIDEBAR_SECTION.DOCK:
        return (
          <TransferDock
            workspace={workspace}
            canMutate={false}
            onPlaceProduct={() => {}}
          />
        );

      case SIDEBAR_SECTION.PRODUCT_LIST:
        return (
          <ProductList
            operationType="transfer"
            operationGuid={workspace.guid}
            state={state}
            reportDisabled
          />
        );

      default:
        <Typography color="text.secondary" textAlign="center" py={4}>
          بخش انتخاب‌شده یافت نشد.
        </Typography>;
    }
  };

  return (
    <RequireOperationSettings>
      {/* operation header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          width: 'full',
          mt: 2,
        }}
      >
        {/* page title and status of operation */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: '600' }}>
            ارسال مستقیم &quot;{workspace?.title}&quot;
          </Typography>
          {<Chip size="small" color={status.color} label={status.label} />}
        </Box>

        {/* action buttons */}
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          {canSubmitOperation ? (
            <LoadingButton
              variant="contained"
              color="warning"
              loading={loading || actionLoading}
              disabled={loading || actionLoading}
              onClick={() => setIsFinishModalOpen(true)}
            >
              اتمام عملیات
            </LoadingButton>
          ) : null}
        </Stack>
      </Box>

      <Divider orientation="horizontal" variant="fullWidth" sx={{ my: 2 }} />

      {/* operation body */}
      <Box sx={{ display: 'flex', height: '85vh', gap: 2 }}>
        <TransferWorkspaceSidebar
          selectedSection={selectedSection}
          onSelectSidebarItem={handleSelectSidebarItem}
        />

        <Divider orientation="vertical" variant="fullWidth" flexItem />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            flex: {
              xs: '2',
              sm: '3',
              md: '4',
              lg: '5',
            },
            height: '100%',
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <Typography sx={{ fontSize: 18, fontWeight: '600' }}>
              {sectionTitle}{' '}
            </Typography>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
            {renderMainContent()}
          </Box>
        </Box>
      </Box>

      <FinishOperationModal
        open={isFinishModalOpen}
        onClose={() => setIsFinishModalOpen(false)}
        onConfirm={handleConfirmFinishOperation}
        loading={actionLoading}
        title={title}
      />
    </RequireOperationSettings>
  );
};

export default TransferOperationWorkspace;
